import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { TrendingDown, Info } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import { computeWaterfall, fmtMoney, STAGE_COLORS } from '../../lib/valueConversion';

const KIND_COLORS = { total: '#0e7490', loss: '#e11d48', gain: '#10b981' };

/**
 * Missing Middle Loss Waterfall — the hero visual of the Value
 * Conversion Analysis page. Shows where dollar value leaked along the
 * journey from potential (largest saved scenario) to realized value.
 */
export function LossWaterfall({ gapRows, stages, potential }) {
  const wf = computeWaterfall(gapRows, stages, potential);
  if (!wf) return null;

  // Build floating-bar data: each loss bar floats from (running − loss) → running.
  let running = wf.potential;
  const data = wf.steps.map((s) => {
    if (s.kind === 'total') {
      return { name: s.label, base: 0, value: s.amount, kind: s.kind, key: s.key, amount: s.amount };
    }
    const base = running - s.amount;
    const row = { name: s.label, base, value: s.amount, kind: s.kind, key: s.key, amount: -s.amount };
    running = base;
    return row;
  });

  const barColor = (d) =>
    d.kind === 'total' ? KIND_COLORS.total : (d.key && STAGE_COLORS[d.key]) || KIND_COLORS.loss;

  return (
    <Card data-testid="loss-waterfall-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingDown className="h-4 w-4 text-rose-600" />
          Missing Middle Loss Waterfall
        </CardTitle>
        <CardDescription>
          The value wasn't lost at the end — it leaked throughout the journey.{' '}
          <span className="font-semibold text-slate-700">
            {fmtMoney(wf.potential)} potential → {fmtMoney(wf.actual)} realized ({wf.realizationPct}% conversion)
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 24, right: 12, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10.5 }} interval={0} angle={-14} textAnchor="end" height={52} />
              <YAxis tickFormatter={(v) => fmtMoney(v)} tick={{ fontSize: 11 }} width={64} />
              <Tooltip
                formatter={(value, name, props) => {
                  if (name === 'base') return [null, null];
                  const d = props?.payload;
                  return [fmtMoney(d?.kind === 'total' ? d.amount : d.amount), d?.kind === 'total' ? 'Value' : 'Leak'];
                }}
                labelStyle={{ fontSize: 12, fontWeight: 600 }}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
              <Bar dataKey="value" stackId="wf" radius={[3, 3, 0, 0]}>
                {data.map((d) => (
                  <Cell key={d.name} fill={barColor(d)} fillOpacity={d.kind === 'loss' ? 0.85 : 1} />
                ))}
                <LabelList
                  dataKey="amount"
                  position="top"
                  formatter={(v) => fmtMoney(v)}
                  style={{ fontSize: 10, fontWeight: 700, fill: '#334155' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 flex items-start gap-1.5 text-[11px] text-slate-500 leading-relaxed">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
          <span>
            <strong>Modeled decomposition</strong> — potential value is anchored to your largest saved scenario
            ({potential?.scenario_name || '—'}); realized value applies your measured KPI conversion rate; stage
            losses are allocated from your live journey-stage scores (85%) plus execution variance (15%).
          </span>
        </p>
      </CardContent>
    </Card>
  );
}
